import type { ModelBase } from './modelBase';

export interface Input extends ModelBase {
  name: string;
  enabled: boolean;
  createdDate?: string;
  modifiedDate?: string;
  identifier: string;
  actions?: [];
  scheduleId?: string | null;
  entityOuId: string;
  deleted: boolean;
  scheduleICSData?: string | null;
  description: string;
  overallStatus?: string;
}

export interface InputLiveStatusResponse {
  inputId?: string;
  overallStatus?: string;
  liveProgramIds?: string[];
}

export enum InputOverallStatus {
  Disconnected = 'Disconnected',
  Connected = 'Connected',
  Starting = 'Starting',
  Started = 'Started',
  Stopping = 'Stopping',
}

export interface InputDisplayStatus {
  showLiveBadge: boolean;
  label: string;
}

export function isInputOverallStatusDisconnected(overallStatus?: string | null): boolean {
  return overallStatus?.toLowerCase() === InputOverallStatus.Disconnected.toLowerCase();
}

export function isInputOverallStatusStarted(overallStatus?: string | null): boolean {
  return overallStatus?.toLowerCase() === InputOverallStatus.Started.toLowerCase();
}

export function isInputOverallStatusStarting(overallStatus?: string | null): boolean {
  return overallStatus?.toLowerCase() === InputOverallStatus.Starting.toLowerCase();
}

export function isInputOverallStatusStopping(overallStatus?: string | null): boolean {
  return overallStatus?.toLowerCase() === InputOverallStatus.Stopping.toLowerCase();
}

/** True when a manual live session is active or transitioning (Starting, Started, or Stopping). */
export function isInputOverallStatusLiveSessionActive(overallStatus?: string | null): boolean {
  return (
    isInputOverallStatusStarting(overallStatus) ||
    isInputOverallStatusStarted(overallStatus) ||
    isInputOverallStatusStopping(overallStatus)
  );
}

/** True when the input source is available for preview. */
export function isInputOverallStatusConnected(overallStatus?: string | null): boolean {
  if (!overallStatus) {
    return false;
  }
  const normalized = overallStatus.toLowerCase();
  return (
    normalized === InputOverallStatus.Connected.toLowerCase() ||
    normalized === InputOverallStatus.Starting.toLowerCase() ||
    normalized === InputOverallStatus.Started.toLowerCase() ||
    normalized === InputOverallStatus.Stopping.toLowerCase()
  );
}

/** @deprecated Use isInputOverallStatusConnected */
export function isInputSourceConnected(overallStatus?: string | null): boolean {
  return isInputOverallStatusConnected(overallStatus);
}

export function getInputDisplayStatus(overallStatus?: string | null): InputDisplayStatus {
  if (isInputOverallStatusStarted(overallStatus)) {
    return { showLiveBadge: true, label: 'Live' };
  }

  if (isInputOverallStatusStopping(overallStatus)) {
    return { showLiveBadge: false, label: 'Stopping' };
  }

  if (isInputOverallStatusStarting(overallStatus)) {
    return { showLiveBadge: false, label: 'Starting' };
  }

  if (overallStatus?.toLowerCase() === InputOverallStatus.Connected.toLowerCase()) {
    return { showLiveBadge: false, label: 'Connected' };
  }

  if (isInputOverallStatusDisconnected(overallStatus)) {
    return { showLiveBadge: false, label: 'No Connection' };
  }

  return { showLiveBadge: false, label: '' };
}

/** Brief debug log for overallStatus → display label mapping. */
export function logInputOverallStatus(context: string, overallStatus?: string | null): void {
  const displayStatus = getInputDisplayStatus(overallStatus);
  console.log(`[InputStatus] ${context}`, {
    overallStatus,
    label: displayStatus.label,
    showLiveBadge: displayStatus.showLiveBadge,
  });
}

export interface LivePreviewStartRequest {
  inputId: string;
  ouId: string;
}

export interface LivePreviewStartResponse {
  jobId: string;
  livePreviewUrl: string;
}
